import type { JSX, ReactNode } from 'react';
import { useState } from 'react';

export interface CollapsibleSectionProps {
  /**
   * Section title displayed in the header
   */
  title: string;

  /**
   * Content to display when section is expanded
   */
  children: ReactNode;

  /**
   * Whether the section is expanded by default
   * @default false
   */
  defaultExpanded?: boolean;

  /**
   * Optional additional CSS classes for the container
   */
  className?: string;
}

/**
 * CollapsibleSection - reusable collapsible section with header and toggle
 *
 * Features:
 * - Click to expand/collapse content
 * - Visual indicator (+/−) for expand state
 * - Configurable default state
 * - Consistent styling with border separator
 *
 * @example
 * ```tsx
 * <CollapsibleSection title="Pension" defaultExpanded={true}>
 *   <PensionInput ... />
 * </CollapsibleSection>
 * ```
 */
export function CollapsibleSection({
  title,
  children,
  defaultExpanded = false,
  className = '',
}: CollapsibleSectionProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className={`border-t pt-4 ${className}`}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left mb-4"
        aria-expanded={isExpanded}
      >
        <h3 className="text-md font-semibold text-gray-900">{title}</h3>
        <span className="text-gray-500" aria-hidden="true">
          {isExpanded ? '−' : '+'}
        </span>
      </button>
      {isExpanded && children}
    </div>
  );
}
